import { useState, useCallback } from 'react';
import { formatDateForInput } from '../../../../utils/dateUtils';

export const useAuditDetailsHandlers = (
  processedAudit,
  updateAudit,
  refetchAudit,
  setSnackbarInfo
) => {
  const [editingAuditor, setEditingAuditor] = useState(false);
  const [editingEvidenceStart, setEditingEvidenceStart] = useState(false);
  const [editingEvidenceEnd, setEditingEvidenceEnd] = useState(false);
  // tempAuditors stores full { id, name, email, is_internal } objects
  const [tempAuditors, setTempAuditors] = useState([]);
  const [tempEvidenceStart, setTempEvidenceStart] = useState("");
  const [tempEvidenceEnd, setTempEvidenceEnd] = useState("");

  // Auditor handlers
  const handleEditAuditor = useCallback(() => {
    setTempAuditors(processedAudit?.auditors || []);
    setEditingAuditor(true);
  }, [processedAudit?.auditors]);

  const handleSaveAuditor = useCallback(async () => {
    try {
      const input = [{
        id: processedAudit.id,
        auditors: tempAuditors.map((a) => ({ id: String(a.id), is_internal: a.is_internal })),
      }];

      const result = await updateAudit({
        variables: { input },
      });

      if (result.data?.updateAudit?.success) {
        await refetchAudit();
        setEditingAuditor(false);
      } else {
        const errorMessage = result.data?.updateAudit?.data?.[0]?.error || result.data?.updateAudit?.message || "Failed to update auditor";
        console.error("Failed to update auditor:", errorMessage);
        if (setSnackbarInfo) {
          setSnackbarInfo({
            message: errorMessage,
            vertical: "top",
            horizontal: "right",
          });
        }
        setEditingAuditor(false);
      }
    } catch (error) {
      console.error("Failed to update auditor:", error);
      if (setSnackbarInfo) {
        setSnackbarInfo({
          message: error.message || "An error occurred while updating auditor",
          vertical: "top",
          horizontal: "right",
        });
      }
      setEditingAuditor(false);
    }
  }, [processedAudit?.id, tempAuditors, updateAudit, refetchAudit, setSnackbarInfo]);

  const handleCancelAuditor = useCallback(() => {
    setEditingAuditor(false);
    setTempAuditors([]);
  }, []);

  // Audit Start handlers
  const handleEditEvidenceStart = useCallback(() => {
    setTempEvidenceStart(formatDateForInput(processedAudit?.audit_start_date) || "");
    setEditingEvidenceStart(true);
  }, [processedAudit?.audit_start_date]);

  const handleSaveEvidenceStart = useCallback(async () => {
    try {
      const input = [{
        id: processedAudit.id,
        audit_start_date: tempEvidenceStart,
      }];

      const result = await updateAudit({
        variables: { input },
      });

      if (result.data?.updateAudit?.success) {
        await refetchAudit();
        setEditingEvidenceStart(false);
      } else {
        const errorMessage = result.data?.updateAudit?.data?.[0]?.error || result.data?.updateAudit?.message || "Failed to update evidence start date";
        console.error("Failed to update evidence start date:", errorMessage);
        if (setSnackbarInfo) {
          setSnackbarInfo({
            message: errorMessage,
            vertical: "top",
            horizontal: "right",
          });
        }
        setEditingEvidenceStart(false);
      }
    } catch (error) {
      console.error("Failed to update evidence start date:", error);
      if (setSnackbarInfo) {
        setSnackbarInfo({
          message: error.message || "An error occurred while updating evidence start date",
          vertical: "top",
          horizontal: "right",
        });
      }
      setEditingEvidenceStart(false);
    }
  }, [processedAudit?.id, tempEvidenceStart, updateAudit, refetchAudit, setSnackbarInfo]);

  const handleCancelEvidenceStart = useCallback(() => {
    setEditingEvidenceStart(false);
    setTempEvidenceStart("");
  }, []);

  // Audit End handlers
  const handleEditEvidenceEnd = useCallback(() => {
    setTempEvidenceEnd(formatDateForInput(processedAudit?.audit_end_date) || "");
    setEditingEvidenceEnd(true);
  }, [processedAudit?.audit_end_date]);

  const handleSaveEvidenceEnd = useCallback(async () => {
    try {
      const input = [{
        id: processedAudit.id,
        audit_end_date: tempEvidenceEnd,
      }];

      const result = await updateAudit({
        variables: { input },
      });

      if (result.data?.updateAudit?.success) {
        await refetchAudit();
        setEditingEvidenceEnd(false);
      } else {
        const errorMessage = result.data?.updateAudit?.data?.[0]?.error || result.data?.updateAudit?.message || "Failed to update evidence end date";
        console.error("Failed to update evidence end date:", errorMessage);
        if (setSnackbarInfo) {
          setSnackbarInfo({
            message: errorMessage,
            vertical: "top",
            horizontal: "right",
          });
        }
        setEditingEvidenceEnd(false);
      }
    } catch (error) {
      console.error("Failed to update evidence end date:", error);
      if (setSnackbarInfo) {
        setSnackbarInfo({
          message: error.message || "An error occurred while updating evidence end date",
          vertical: "top",
          horizontal: "right",
        });
      }
      setEditingEvidenceEnd(false);
    }
  }, [processedAudit?.id, tempEvidenceEnd, updateAudit, refetchAudit, setSnackbarInfo]);

  const handleCancelEvidenceEnd = useCallback(() => {
    setEditingEvidenceEnd(false);
    setTempEvidenceEnd("");
  }, []);

  return {
    // Auditor
    editingAuditor,
    tempAuditors,
    setTempAuditors,
    handleEditAuditor,
    handleSaveAuditor,
    handleCancelAuditor,
    // Evidence Start
    editingEvidenceStart,
    tempEvidenceStart,
    setTempEvidenceStart,
    handleEditEvidenceStart,
    handleSaveEvidenceStart,
    handleCancelEvidenceStart,
    // Evidence End
    editingEvidenceEnd,
    tempEvidenceEnd,
    setTempEvidenceEnd,
    handleEditEvidenceEnd,
    handleSaveEvidenceEnd,
    handleCancelEvidenceEnd,
  };
};
